import type { Resource } from '@/features/Collaborators/Models';
import type { CapabilityCatalog } from '@/features/Collaborators/Data/hooks/useCapabilityCatalogQuery';

export type ResourceLike = Partial<Resource> & {
  typeId?: string | null;
  technologyId?: string | null;
  seniorityId?: string | null;
  isPrimary?: boolean | null;
  resourceTypeName?: string | null;
  technologyName?: string | null;
  seniorityName?: string | null;
};

export type ResolvedResource = Resource & {
  resourceTypeName?: string | null;
  technologyName?: string | null;
  seniorityName?: string | null;
};

export interface GroupedResourcesBySeniority {
  seniorityName: string;
  resources: ResolvedResource[];
  technologyNames: string[];
}

export interface TechnologyProficiencyEntry {
  key: string;
  technologyName: string;
  seniorityName: string | null;
  isPrimary: boolean;
}

const readLabel = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

export const resolveTechnologyLabel = (resource?: ResourceLike | null): string | null =>
  readLabel(resource?.technologyName) ?? readLabel(resource?.technologyId);

export const resolveResourceTypeLabel = (resource?: ResourceLike | null): string | null =>
  readLabel(resource?.resourceTypeName) ?? readLabel(resource?.typeId);

export const resolveSeniorityLabel = (resource?: ResourceLike | null): string | null =>
  readLabel(resource?.seniorityName) ?? readLabel(resource?.seniorityId);

export const getPrimaryResource = <T extends ResourceLike>(
  resources?: T[] | null,
): T | null => {
  if (!resources || resources.length === 0) {
    return null;
  }

  return resources.find((resource) => resource?.isPrimary) ?? resources[0] ?? null;
};

export const getSeniorityEmoji = (seniorityName?: string | null): string => {
  if (!seniorityName) return '❓';

  const lower = seniorityName.toLowerCase();
  if (lower.includes('estagiário') || lower.includes('trainee')) return '🌱';
  if (lower.includes('junior') || lower.includes('júnior') || lower.includes('jr')) return '🌿';
  if (lower.includes('pleno') || lower.includes('mid')) return '🌳';
  if (lower.includes('senior') || lower.includes('sênior') || lower.includes('sr')) return '🌲';
  if (lower.includes('specialist') || lower.includes('especialista')) return '🏆';
  if (lower.includes('lead') || lower.includes('tech lead')) return '👑';
  return '⭐';
};

export const buildCapabilityLabel = (
  resource?: ResourceLike | null,
  options?: { includeType?: boolean }
): string | null => {
  if (!resource) {
    return null;
  }

  const includeType = options?.includeType ?? true;
  const technologyName = resolveTechnologyLabel(resource);
  const resourceTypeName = includeType ? resolveResourceTypeLabel(resource) : null;
  const parts = [technologyName, resourceTypeName].filter(Boolean);

  return parts.length > 0 ? parts.join(' • ') : null;
};

export const buildPrimarySkillLabel = (
  resources?: ResourceLike[] | null,
  options?: { includeType?: boolean }
): string | null => buildCapabilityLabel(getPrimaryResource(resources), options);

export const buildTechnologyList = (resources?: ResourceLike[] | null): string[] => {
  if (!resources?.length) {
    return [];
  }

  const uniqueTechnologies = new Set<string>();
  resources.forEach((resource) => {
    const technologyName = resolveTechnologyLabel(resource);
    if (technologyName) {
      uniqueTechnologies.add(technologyName);
    }
  });

  return Array.from(uniqueTechnologies);
};

export const buildCapabilityFilterValues = (resources?: ResourceLike[] | null): string[] => {
  if (!resources?.length) {
    return [];
  }

  const uniqueCapabilities = new Set<string>();

  resources.forEach((resource) => {
    const technologyName = resolveTechnologyLabel(resource);
    const resourceTypeName = resolveResourceTypeLabel(resource);
    const label = [technologyName, resourceTypeName].filter(Boolean).join(' • ');

    if (label) {
      uniqueCapabilities.add(label);
    }
  });

  return Array.from(uniqueCapabilities);
};

export const formatTechnologyProficiencyLabel = (
  capability: Pick<TechnologyProficiencyEntry, 'technologyName' | 'seniorityName'>,
): string => {
  if (!capability.seniorityName) {
    return capability.technologyName;
  }

  return `${capability.technologyName} • ${capability.seniorityName}`;
};

export const buildTechnologyProficiencyEntries = (
  resources?: ResourceLike[] | null,
): TechnologyProficiencyEntry[] => {
  if (!resources?.length) {
    return [];
  }

  const uniqueCapabilities = new Map<string, TechnologyProficiencyEntry>();

  resources.forEach((resource) => {
    const technologyName = resolveTechnologyLabel(resource);
    if (!technologyName) {
      return;
    }

    const seniorityName = resolveSeniorityLabel(resource);
    const key = `${technologyName}::${seniorityName ?? ''}`;
    const existingEntry = uniqueCapabilities.get(key);

    if (existingEntry) {
      existingEntry.isPrimary = existingEntry.isPrimary || Boolean(resource.isPrimary);
      return;
    }

    uniqueCapabilities.set(key, {
      key,
      technologyName,
      seniorityName,
      isPrimary: Boolean(resource.isPrimary),
    });
  });

  return Array.from(uniqueCapabilities.values()).sort((left, right) => {
    if (left.isPrimary !== right.isPrimary) {
      return left.isPrimary ? -1 : 1;
    }

    const technologyComparison = left.technologyName.localeCompare(right.technologyName, 'pt-BR', {
      sensitivity: 'base',
    });
    if (technologyComparison !== 0) {
      return technologyComparison;
    }

    return (left.seniorityName ?? '').localeCompare(right.seniorityName ?? '', 'pt-BR', {
      sensitivity: 'base',
    });
  });
};

export const groupResourcesBySeniority = (
  resources?: ResolvedResource[] | null,
): GroupedResourcesBySeniority[] => {
  if (!resources?.length) {
    return [];
  }

  const grouped = new Map<string, GroupedResourcesBySeniority>();

  resources.forEach((resource) => {
    const seniorityName = resolveSeniorityLabel(resource) ?? 'Sem senioridade';
    const currentGroup = grouped.get(seniorityName) ?? {
      seniorityName,
      resources: [],
      technologyNames: [],
    };

    currentGroup.resources.push(resource);

    const technologyName = resolveTechnologyLabel(resource);
    if (technologyName && !currentGroup.technologyNames.includes(technologyName)) {
      currentGroup.technologyNames.push(technologyName);
    }

    grouped.set(seniorityName, currentGroup);
  });

  return Array.from(grouped.values());
};

export const buildSkillsSummary = (resources?: ResolvedResource[] | null): string | null => {
  const grouped = groupResourcesBySeniority(resources);
  if (grouped.length === 0) {
    return null;
  }

  return grouped
    .map((group) => `${group.seniorityName}: ${group.technologyNames.join(', ')}`)
    .join(' | ');
};

export const resolveResources = (
  resources?: ResourceLike[] | null,
  catalog?: CapabilityCatalog | null,
): ResolvedResource[] => {
  if (!resources?.length) {
    return [];
  }

  return resources.map((resource) => {
    const typeId = readLabel(resource.typeId) ?? '';
    const technologyId = readLabel(resource.technologyId) ?? '';
    const seniorityId = readLabel(resource.seniorityId) ?? '';

    return {
      typeId,
      technologyId,
      seniorityId,
      isPrimary: Boolean(resource.isPrimary),
      ...resource,
      resourceTypeName:
        readLabel(resource.resourceTypeName) ??
        (typeId ? catalog?.resourceTypeMap.get(typeId) ?? null : null),
      technologyName:
        readLabel(resource.technologyName) ??
        (technologyId ? catalog?.technologyMap.get(technologyId) ?? null : null),
      seniorityName:
        readLabel(resource.seniorityName) ??
        (seniorityId ? catalog?.seniorityMap.get(seniorityId) ?? null : null),
    };
  });
};

export const resolveCollaboratorResources = <T extends { resources?: ResourceLike[] | null }>(
  collaborator: T,
  catalog?: CapabilityCatalog | null,
): T & { resources?: ResolvedResource[] } => {
  if (!collaborator.resources) {
    return collaborator as T & { resources?: ResolvedResource[] };
  }

  return {
    ...collaborator,
    resources: resolveResources(collaborator.resources as ResourceLike[], catalog),
  };
};
